import React, { useState, useEffect } from 'react';
import SessionsTable from './SessionsTable';
import './CompletedSessions.scss';
import { getAnalyzeActAPIUrl, getAPIUrl } from "../../api/apiUtils";
import { apiFetch } from "../../api/apiClient";

const API_BASE_URL = getAPIUrl();

const CompletedSessions = () => {
    const [jobTypes, setJobTypes] = useState([]);
    const [sessionsByType, setSessionsByType] = useState({});
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [limit, setLimit] = useState(25);
    const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
        fetchJobTypes();
    }, []);

    useEffect(() => {
        if (activeTab !== 'all' && jobTypes.length > 0) {
            fetchSessionsForType(activeTab, 1);
        }
    }, [activeTab, limit]);

    const getPage = (jobType) => (pagination[jobType]?.page || 1);
    const getTotalPages = (jobType) => (pagination[jobType]?.total_pages || 1);
    const getTotal = (jobType) => (pagination[jobType]?.total || 0);

    const fetchJobTypes = async () => {
        try {
            const response = await apiFetch(`${API_BASE_URL}/monitoring/job-types`);
            if (!response.ok) throw new Error('Не удалось загрузить типы задач');
            const types = await response.json();
            setJobTypes(types);
            if (types.length > 0 && activeTab === 'all') {
                setActiveTab(types[0]);
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchSessionsForType = async (jobType, page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const offset = (page - 1) * limit;
            const response = await apiFetch(`${API_BASE_URL}/monitoring/sessions/completed/${jobType}?limit=${limit}&offset=${offset}`);
            if (!response.ok) throw new Error(`Ошибка при загрузке сессий для ${jobType}`);
            const data = await response.json();
            setSessionsByType(prev => ({ ...prev, [jobType]: data.sessions }));
            setPagination(prev => ({
                ...prev,
                [jobType]: {
                    page: data.page,
                    total_pages: data.total_pages,
                    total: data.total
                }
            }));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (jobType, newPage) => {
        fetchSessionsForType(jobType, newPage);
    };

    const renderTable = (sessions, jobType) => {
        if (!sessions || sessions.length === 0) {
            return (
                <div className="empty-state">
                    <i className="fas fa-inbox"></i>
                    <p>Нет завершенных сессий для отображения</p>
                </div>
            );
        }

        const columns = ['session_id', 'group_id', 'group_name', 'meta_id', 'start_date', 'end_date', 'created_at'];

        return (
            <>
                <SessionsTable
                    sessions={sessions}
                    loading={loading}
                    error={error}
                    onRetry={() => fetchSessionsForType(jobType, getPage(jobType))}
                    columns={columns}
                    isGroupStats={false}
                />

                <div className="pagination-bar">
                    <div className="info">Всего: {getTotal(jobType)} записей</div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => handlePageChange(jobType, getPage(jobType) - 1)}
                            disabled={getPage(jobType) <= 1 || loading}
                        >
                            &lt;
                        </button>
                        <span>{getPage(jobType)} / {getTotalPages(jobType)}</span>
                        <button
                            onClick={() => handlePageChange(jobType, getPage(jobType) + 1)}
                            disabled={getPage(jobType) >= getTotalPages(jobType) || loading}
                        >
                            &gt;
                        </button>
                    </div>
                </div>
            </>
        );
    };

    if (loading && (!sessionsByType[activeTab] || sessionsByType[activeTab].length === 0)) {
        return (
            <div className="loading-state">
                <i className="fas fa-circle-notch fa-spin"></i>
                <p>Загрузка завершенных сессий...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-state">
                <i className="fas fa-exclamation-triangle"></i>
                <p>{error}</p>
                <button onClick={() => fetchSessionsForType(activeTab, getPage(activeTab))}>
                    Повторить
                </button>
            </div>
        );
    }

    return (
        <div className="completed-sessions">
            {/* Заголовок */}
            <div className="completed-header">
                <h2>
                    <i className="fas fa-check-circle"></i>
                    Завершенные сессии
                </h2>
                <p>Просмотр завершенных сессий по типам задач</p>
            </div>

            {/* Панель фильтров */}
            <div className="completed-filters">
                <label>Лимит:</label>
                <select
                    value={limit}
                    onChange={(e) => {
                        const newLimit = parseInt(e.target.value);
                        setLimit(newLimit);
                        if (activeTab !== 'all') {
                            setPagination(prev => ({
                                ...prev,
                                [activeTab]: { ...prev[activeTab], page: 1 }
                            }));
                        }
                    }}
                >
                    <option value={10}>10 записей</option>
                    <option value={25}>25 записей</option>
                    <option value={50}>50 записей</option>
                    <option value={100}>100 записей</option>
                    <option value={200}>200 записей</option>
                    <option value={500}>500 записей</option>
                </select>
            </div>

            {/* Вкладки */}
            {jobTypes.length > 0 ? (
            <>
                <nav className="completed-tabs">
                {jobTypes.map(jobType => (
                    <button
                    key={jobType}
                    onClick={() => setActiveTab(jobType)}
                    className={`completed-tab ${activeTab === jobType ? 'active' : ''}`}
                    >
                    <i className="fas fa-tag"></i>
                    {jobType} {getTotal(jobType) ? `(${getTotal(jobType)})` : ''}
                    </button>
                ))}
                </nav>

                <div className="completed-content">
                {activeTab && sessionsByType[activeTab] && (
                    <>
                    <div className="task-info-box">
                        <h3>
                        <i className="fas fa-tag"></i>
                        Тип задачи: <span>{activeTab}</span>
                        </h3>
                        <p>Количество завершённых сессий: <b>{getTotal(activeTab)}</b></p>
                    </div>

                    {renderTable(sessionsByType[activeTab], activeTab)}
                    </>
                )}
                </div>
            </>
            ) : (
            <div className="empty-state">
                <i className="fas fa-inbox"></i>
                <p>Нет завершённых сессий для отображения</p>
            </div>
            )}

        </div>
    );
};

export default CompletedSessions;
